import React, { useCallback, useEffect, useState } from 'react';
import './PwaInstallPrompt.css';

const STORAGE_KEY = 'svirrens_pwa_install_dismissed_until';
const STORAGE_IOS_KEY = 'svirrens_pwa_ios_hint_dismissed_until';
const DISMISS_DAYS = 30;

function getDismissedUntil(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    return parseInt(raw, 10) || 0;
  } catch {
    return 0;
  }
}

function setDismissed(key) {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(key, String(until));
  } catch {
    /* ignore */
  }
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/** iPhone, iPod, iPad (inkl. iPadOS der kan melde sig som Mac) */
function isIosLike() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Chrome/Edge: beforeinstallprompt → egen "Installer"-knap.
 * iOS Safari: den hændelse findes ikke — vi viser i stedet vejledning om Del-menu.
 */
export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedUntil = getDismissedUntil(STORAGE_KEY);
    if (dismissedUntil > Date.now()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    if (!isIosLike()) return;
    if (getDismissedUntil(STORAGE_IOS_KEY) > Date.now()) return;
    setShowIosHint(true);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice.catch(() => {});
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismissChrome = useCallback(() => {
    setDismissed(STORAGE_KEY);
    setDeferredPrompt(null);
  }, []);

  const handleDismissIos = useCallback(() => {
    setDismissed(STORAGE_IOS_KEY);
    setShowIosHint(false);
  }, []);

  if (deferredPrompt) {
    return (
      <div className="pwa-install-prompt" role="dialog" aria-label="Installer app">
        <div className="pwa-install-prompt__inner">
          <p className="pwa-install-prompt__text">
            Installer Svirr på din telefon eller tablet — hurtig adgang som en app.
          </p>
          <div className="pwa-install-prompt__actions">
            <button type="button" className="pwa-install-prompt__btn pwa-install-prompt__btn--secondary" onClick={handleDismissChrome}>
              Ikke nu
            </button>
            <button type="button" className="pwa-install-prompt__btn pwa-install-prompt__btn--primary" onClick={handleInstall}>
              Installer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="pwa-install-prompt" role="dialog" aria-label="Tilføj til hjemmeskærm">
        <div className="pwa-install-prompt__inner">
          <p className="pwa-install-prompt__text">
            Tryk på <strong>Del</strong> i Safari (eller i Chrome), og vælg{' '}
            <strong>Føj til hjemmeskærm</strong> — så åbner Svirr som app på telefonen.
          </p>
          <div className="pwa-install-prompt__actions">
            <button type="button" className="pwa-install-prompt__btn pwa-install-prompt__btn--secondary" onClick={handleDismissIos}>
              Forstået
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
